import { useCallback, useEffect, useRef } from "react";

function findScrollParent(element: HTMLElement): HTMLElement | null {
  let node = element.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

function wheelDeltaPx(event: WheelEvent, scroller: HTMLElement): number {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * 16;
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * scroller.clientHeight;
  return event.deltaY;
}

/**
 * Scrolls the page instead of the element until the element's leading edge
 * reaches the scrollport, so a viewport-sized inner scroller can't trap the wheel.
 *
 * Returns a callback ref — attach it to the nested scroller:
 *   const previewRef = useOuterScrollUntilInView();
 *   <Box ref={previewRef}>…</Box>
 *
 * The callback-ref form (rather than a passed-in RefObject) attaches the
 * listener when the node mounts, so conditionally rendered content is covered.
 */
export function useOuterScrollUntilInView(): (el: HTMLElement | null) => void {
  const currentElRef = useRef<HTMLElement | null>(null);
  const currentHandlerRef = useRef<((event: WheelEvent) => void) | null>(null);

  const detach = useCallback(() => {
    const el = currentElRef.current;
    const handler = currentHandlerRef.current;
    if (el && handler) {
      el.removeEventListener("wheel", handler);
    }
    currentElRef.current = null;
    currentHandlerRef.current = null;
  }, []);

  useEffect(() => detach, [detach]);

  return useCallback(
    (element: HTMLElement | null) => {
      detach();
      if (!element) return;

      const onWheel = (event: WheelEvent) => {
        if (event.deltaY === 0 || event.ctrlKey) return;
        const scroller = findScrollParent(element);
        if (!scroller) return;

        const delta = wheelDeltaPx(event, scroller);
        const down = delta > 0;
        const maxScrollTop = scroller.scrollHeight - scroller.clientHeight;
        const pageCanMove = down ? scroller.scrollTop < maxScrollTop - 1 : scroller.scrollTop > 1;
        if (!pageCanMove) return;

        const inner = element.getBoundingClientRect();
        const outer = scroller.getBoundingClientRect();
        // How far the page still has to move before the element's leading edge
        // (top when scrolling down, bottom when scrolling up) meets the scrollport.
        const remaining = down ? inner.top - outer.top : inner.bottom - outer.bottom;
        if (down ? remaining <= 1 : remaining >= -1) return;

        event.preventDefault();
        // Clamped so the element lands aligned rather than overshooting past it.
        scroller.scrollTop += down ? Math.min(delta, remaining) : Math.max(delta, remaining);
      };

      element.addEventListener("wheel", onWheel, { passive: false });
      currentElRef.current = element;
      currentHandlerRef.current = onWheel;
    },
    [detach]
  );
}
